/**
 * Inserting catalog blocks from the Blocks palette — by click (after the cursor's block, as
 * CdmToolbar's insertBlockAfterCursor does) or by drop (the block boundary nearest the pointer,
 * as a dropped loop scaffold lands). An empty paragraph at the cursor or under the drop is replaced
 * instead (cdm/emptyParagraph.ts). Either way the whole block is one transaction with its own
 * history step, so a single undo removes it.
 *
 * A `setup` block (data-bound: line items, totals, repeating, conditional) asks the registered
 * setup handler — the BindingSetupDialog — to collect its binding first, remembering the drop
 * position; the dialog inserts with insertBlocks on confirm. Without a handler the default builds.
 */
import { findMovableBlock, MOVABLE_CONTAINERS } from '../cdm/blockMove'
import { Extension, type Editor, type JSONContent } from '@tiptap/core'
import { Fragment, Slice } from '@tiptap/pm/model'
import { NodeSelection, Plugin, PluginKey, Selection } from '@tiptap/pm/state'
import { closeHistory } from '@tiptap/pm/history'
import { dropPoint } from '@tiptap/pm/transform'
import { catalogBlock, type CatalogBlock } from './catalog'
import type { BindingSetup, BlockSetup } from './bindingSetup'
import { emptyParagraphAtDrop, emptyParagraphAtSelection, type BlockRange } from '../cdm/emptyParagraph'

export const PALETTE_BLOCK_MIME = 'application/x-docmorphia-palette-block'

/** `replaceEmpty`: `pos` is the start of an empty paragraph the image should replace. */
type UploadRequest = (pos: number, replaceEmpty: boolean) => void
const uploadRequests = new WeakMap<Editor, UploadRequest>()

/** Registers what an `upload` block (Image) does when clicked or dropped — the panel's file picker. */
export function setPaletteUploadHandler(editor: Editor, handler: UploadRequest | null) {
  if (handler) uploadRequests.set(editor, handler)
  else uploadRequests.delete(editor)
}

/**
 * What the setup dialog is asked to do: set up a new block (at `pos`, a drop, or the cursor), or
 * edit the generated structure spanning `from`–`to`, whose binding reads back as `current`.
 */
export type SetupRequest =
  | { kind: BlockSetup; pos?: number }
  | { kind: BlockSetup; edit: { from: number; to: number; current: BindingSetup } }

const setupRequests = new WeakMap<Editor, (req: SetupRequest) => void>()

export function setBlockSetupHandler(editor: Editor, handler: ((req: SetupRequest) => void) | null) {
  if (handler) setupRequests.set(editor, handler)
  else setupRequests.delete(editor)
}

export const hasBlockSetupHandler = (editor: Editor) => setupRequests.has(editor)

export function requestBlockSetup(editor: Editor, req: SetupRequest): boolean {
  const handler = setupRequests.get(editor)
  if (!handler || !editor.isEditable) return false
  handler(req)
  return true
}

/**
 * Where a click inserts: after the selected block, or after the movable block holding the cursor — so a cursor in a
 * table cell or list item puts the new block after the whole table or list, never inside it. Dragging can still drop
 * into a cell.
 */
export function insertionPointAfterCursor(editor: Editor, nodes: Fragment): number {
  const { selection, doc } = editor.state
  if (selection instanceof NodeSelection && selection.node.isBlock) return selection.to
  const block = findMovableBlock(doc, selection.from)
  if (block) return block.pos + block.node.nodeSize
  return dropPoint(doc, selection.from, new Slice(nodes, 0, 0)) ?? selection.from
}

function emptyTarget(editor: Editor, pos: number | undefined): BlockRange | null {
  if (pos !== undefined) return emptyParagraphAtDrop(editor.state.doc, pos)
  const empty = emptyParagraphAtSelection(editor.state.selection)
  // A click never lands inside a table cell or list item, so neither does the replacement.
  return empty && MOVABLE_CONTAINERS.has(editor.state.doc.resolve(empty.from).parent.type.name) ? empty : null
}

export function insertCatalogBlock(editor: Editor, block: CatalogBlock | string, pos?: number): boolean {
  const entry = typeof block === 'string' ? catalogBlock(block) : block
  if (!entry || !editor.isEditable) return false
  if (entry.upload) {
    const request = uploadRequests.get(editor)
    if (!request) return false
    const empty = emptyTarget(editor, pos)
    if (empty) { request(empty.from, true); return true }
    const probe = Fragment.from(editor.state.schema.nodes.pageBreak.create())
    request(pos === undefined ? insertionPointAfterCursor(editor, probe) : dropPoint(editor.state.doc, pos, new Slice(probe, 0, 0)) ?? pos, false)
    return true
  }
  if (entry.setup && requestBlockSetup(editor, { kind: entry.setup, pos })) return true
  return insertBlocks(editor, entry.build(), pos)
}

/** Inserts top-level block JSON as one history step, as a click (`pos` undefined) or drop at `pos` would. */
export function insertBlocks(editor: Editor, blocks: JSONContent[], pos?: number): boolean {
  if (!editor.isEditable) return false
  const { state, view } = editor
  let nodes: Fragment
  try {
    nodes = Fragment.from(blocks.map(json => state.schema.nodeFromJSON(json)))
  } catch {
    return false
  }
  if (!nodes.childCount) return false
  if (pos !== undefined) pos = Math.max(0, Math.min(pos, state.doc.content.size))
  const tr = closeHistory(state.tr)
  const empty = emptyTarget(editor, pos)
  let target: number
  try {
    if (empty) {
      target = empty.from
      tr.replaceWith(empty.from, empty.to, nodes)
    } else {
      target = pos === undefined
        ? insertionPointAfterCursor(editor, nodes)
        : dropPoint(state.doc, pos, new Slice(nodes, 0, 0)) ?? pos
      tr.insert(target, nodes)
    }
  } catch {
    return false
  }
  const $start = tr.doc.resolve(Math.min(target, tr.doc.content.size))
  tr.setSelection(Selection.findFrom($start, 1, true) ?? Selection.near($start))
  view.dispatch(tr.scrollIntoView())
  view.focus()
  return true
}

// A team library block drags with the same MIME, its payload JSON rather than a catalog id.
const teamInserts = new WeakMap<Editor, (contentBlockId: string, pos?: number) => void>()

/** Registers what dropping a team block does — the Blocks panel fetches and inserts it. */
export function setTeamBlockInsertHandler(editor: Editor, handler: ((contentBlockId: string, pos?: number) => void) | null) {
  if (handler) teamInserts.set(editor, handler)
  else teamInserts.delete(editor)
}

export function writeTeamBlock(dataTransfer: DataTransfer, contentBlockId: string) {
  dataTransfer.setData(PALETTE_BLOCK_MIME, JSON.stringify({ kind: 'team', contentBlockId }))
  dataTransfer.effectAllowed = 'copy'
}

export function readTeamBlockId(dataTransfer: DataTransfer | null): string | null {
  const raw = dataTransfer?.getData(PALETTE_BLOCK_MIME)
  if (!raw || raw[0] !== '{') return null
  try {
    const v = JSON.parse(raw) as { kind?: unknown; contentBlockId?: unknown }
    return v.kind === 'team' && typeof v.contentBlockId === 'string' ? v.contentBlockId : null
  } catch {
    return null
  }
}

export function readPaletteBlockId(dataTransfer: DataTransfer | null): string | null {
  const id = dataTransfer?.getData(PALETTE_BLOCK_MIME)
  return id && catalogBlock(id) ? id : null
}

export function writePaletteBlock(dataTransfer: DataTransfer, block: CatalogBlock) {
  dataTransfer.setData(PALETTE_BLOCK_MIME, block.id)
  dataTransfer.effectAllowed = 'copy'
}

export const PaletteDrop = Extension.create({
  name: 'paletteDrop',
  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        key: new PluginKey('paletteDrop'),
        props: {
          handleDrop(view, event) {
            const teamId = readTeamBlockId(event.dataTransfer)
            if (teamId) {
              const handler = teamInserts.get(editor)
              if (!editor.isEditable || !handler) return true
              const at = view.posAtCoords({ left: event.clientX, top: event.clientY })
              if (at) handler(teamId, at.pos)
              return true
            }
            const id = readPaletteBlockId(event.dataTransfer)
            if (!id) return false
            if (!editor.isEditable) return true
            const coords = view.posAtCoords({ left: event.clientX, top: event.clientY })
            if (coords) insertCatalogBlock(editor, id, coords.pos)
            return true
          },
        },
      }),
    ]
  },
})
